import React, {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from 'react';
import {Box, Radio, Typography} from '@mui/material';
import {useTranslation} from 'react-i18next';
import {useForm, Controller} from 'react-hook-form';
import {zodResolver} from '@hookform/resolvers/zod';
import ArrTextField from '../../../common/arr-text-field/ArrTextField';
import {ReactComponent as RadioActiveIcon} from '../../../../assets/images/radio-active-icon.svg';
import {ReactComponent as RadioEnableIcon} from '../../../../assets/images/radio-enable-icon.svg';
import {
  reProductionBasicInfoSchema,
  ReProductionBasicInfoFormData,
} from '../../../../schemas/productions/reProductionSchema';
import {
  ReProductionFormData,
  getReProductionTypeOptions,
  ToastType,
  toReProductionTypeCodeEnum,
  fromReProductionTypeCodeEnum,
  ProductionStatus,
} from '../ReProductionUtils';
import {useDispatch} from 'react-redux';
import {setReproductionIsFieldsChanged} from '../../../../redux/slice/reproductionSlice';
import {
  useGetReProductionBasicInfo,
  useSaveReProductionBasicInfo,
} from '../../../../hooks/productions/useReProductions';
import {useLocation, useNavigate} from 'react-router-dom';
import type {SaveReProductionBasicInfoResponse} from '../../../../api/types/reproduction.type';
import './ReProductionBasicInfo.scss';

/** API returns `reProductionID`; older mocks may use `productionID` / `reproductionID`. */
function getEncryptedIdFromSaveBasicInfoResponse(
  saved: SaveReProductionBasicInfoResponse
): string {
  const r = saved as SaveReProductionBasicInfoResponse & {
    reproductionID?: string;
    productionID?: string;
  };
  return (
    r.reProductionID?.trim() ||
    r.reproductionID?.trim() ||
    r.productionID?.trim() ||
    ''
  );
}

interface ReProductionBasicInfoProps {
  productionId: number;
  /** When false, `useGetReProductionBasicInfo` stays disabled (e.g. until edit wizard hydration finishes). */
  allowDetailQuery?: boolean;
  onFormDataChange: (
    updates: Partial<ReProductionFormData>,
    options?: { silent?: boolean }
  ) => void;
  getStatus: (isShowToast: boolean, status: ToastType, message: string) => void;
  goNext: () => void;
  goBack: () => void;
  onStateChange: (state: {
    saveDisabled: boolean;
    nextDisabled: boolean;
  }) => void;
  securityGroupId: number;
  productionSetId: number;
  productionStatusCode?: string;
}

const ReProductionBasicInfo = forwardRef<
  {save: () => void; next: () => void},
  ReProductionBasicInfoProps
>(
  (
    {
      productionId,
      allowDetailQuery = true,
      onFormDataChange,
      getStatus,
      goNext,
      onStateChange,
      securityGroupId,
      productionSetId,
      productionStatusCode,
    },
    ref
  ) => {
    const {t} = useTranslation();
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const location = useLocation();
    const typeOptions = getReProductionTypeOptions();

    const productionIdRef = useRef(productionId);
    productionIdRef.current = productionId;

    // Tracks whether this step has been persisted (matches CreateNewProduction BasicInfo).
    // Once saved (or hydrated in edit mode), Save stays disabled until the user changes something.
    const [isSaved, setIsSaved] = useState<boolean>(productionId > 0);

    const basicInfoQueryParams = useMemo(
      () => ({
        securityGroupId,
        parentSetId: productionSetId,
        reproductionId: productionId || undefined,
      }),
      [securityGroupId, productionSetId, productionId]
    );

    const isBasicInfoQueryEnabled = allowDetailQuery && productionId > 0;

    const {data: fetchedBasicInfo} = useGetReProductionBasicInfo(
      basicInfoQueryParams,
      isBasicInfoQueryEnabled
    );

    const {mutate: saveReProductionBasicInfo, isPending: isSaving} =
      useSaveReProductionBasicInfo();
    const hydratedBasicInfoKeyRef = useRef<string | null>(null);
    const lastStepperControlsRef = useRef<{
      saveDisabled: boolean;
      nextDisabled: boolean;
    } | null>(null);

    const {
      register,
      control,
      watch,
      trigger,
      getValues,
      reset,
      formState: {errors, isDirty},
    } = useForm<ReProductionBasicInfoFormData>({
      resolver: zodResolver(reProductionBasicInfoSchema()),
      mode: 'onChange',
      defaultValues: {
        name: '',
        description: '',
        reProductionType: undefined,
      },
    });

    // Match CreateNewProduction BasicInfo: dirty state drives "unsaved changes" (not lifted field sync)
    useEffect(() => {
      if (isDirty) {
        dispatch(setReproductionIsFieldsChanged({isFieldsChanged: true}));
      }
    }, [isDirty, dispatch]);

    // Pre-fill from API in edit mode (reset + one-way merge to parent for downstream steps)
    useEffect(() => {
      if (!fetchedBasicInfo || productionId <= 0) {
        return;
      }

      const fetchedReProductionType =
        fromReProductionTypeCodeEnum(fetchedBasicInfo.reProductionTypeCode) ??
        undefined;

      const hydrationKey = JSON.stringify({
        productionId: fetchedBasicInfo.productionID,
        productionName: fetchedBasicInfo.productionName || '',
        productionDescription: fetchedBasicInfo.productionDescription || '',
        reProductionType: fetchedReProductionType ?? null,
      });

      if (hydratedBasicInfoKeyRef.current === hydrationKey) {
        return;
      }

      hydratedBasicInfoKeyRef.current = hydrationKey;

      reset(
        {
          name: fetchedBasicInfo.productionName || '',
          description: fetchedBasicInfo.productionDescription || '',
          reProductionType: fetchedReProductionType ?? undefined,
        },
        {
          keepDirty: false,
          keepDefaultValues: false,
        }
      );

      onFormDataChange(
        {
          name: fetchedBasicInfo.productionName || '',
          description: fetchedBasicInfo.productionDescription || '',
          reProductionType: fetchedReProductionType ?? null,
        },
        { silent: true }
      );

      setIsSaved(true);
    }, [fetchedBasicInfo, productionId, reset, onFormDataChange]);

    const watchedName = watch('name');
    const watchedReProductionType = watch('reProductionType');
    const isInProgressOrCompleted =
      productionStatusCode === ProductionStatus.COMPLETED ||
      productionStatusCode === ProductionStatus.INPROGRESS;

    useEffect(() => {
      const isNameFilled = !!(watchedName ?? '').trim();
      const isTypeSelected = watchedReProductionType != null;
      const nextControls = {
        // Match CreateNewProduction BasicInfo: once saved, Save stays off until user edits again.
        saveDisabled:
          isInProgressOrCompleted ||
          !isNameFilled ||
          !isTypeSelected ||
          isSaving ||
          (!isDirty && isSaved),
        nextDisabled: !isNameFilled || !isTypeSelected || isSaving,
      };
      const prev = lastStepperControlsRef.current;
      if (
        prev &&
        prev.saveDisabled === nextControls.saveDisabled &&
        prev.nextDisabled === nextControls.nextDisabled
      ) {
        return;
      }
      lastStepperControlsRef.current = nextControls;
      onStateChange(nextControls);
    }, [
      watchedName,
      watchedReProductionType,
      onStateChange,
      isSaving,
      isDirty,
      isSaved,
      isInProgressOrCompleted,
    ]);

    const buildPayload = (
      values: ReProductionBasicInfoFormData,
      isMarkComplete: boolean
    ) => {
      const reProductionTypeCode = toReProductionTypeCodeEnum(
        values.reProductionType ?? null
      );
      if (reProductionTypeCode === null) return null;

      return {
        parentSecurityGroupId: securityGroupId,
        parentReProductionSetID: productionSetId,
        reProductionName: values.name,
        reProductionID: productionIdRef.current,
        reProductionDescription: values.description || undefined,
        disclaimer: '',
        isMarkComplete,
        isProductionSet: true,
        exportProductionId: 0,
        isExportProduction: false,
        reProductionTypeCode,
      };
    };

    const shouldNavigateToEdit = () => {
      const targetPath = '/Production/Home/ReProduction/Edit';
      return location.pathname !== targetPath;
    };

    // After a successful save/mark-complete, reset RHF (clears isDirty), sync parent silently,
    // and redirect to the edit URL so subsequent interactions work against an existing
    // re-production id (mirrors CreateNewProduction BasicInfo).
    // NOTE: we deliberately do NOT dispatch setReproductionIsFieldsChanged(false) here — the callers
    // dispatch it synchronously right after firing the mutation so that PrivateRoute's
    // pushState monkey-patch re-reads the refreshed `isFieldsChanged` before onSuccess runs.
    const handleSaveSuccess = (
      savedProductionIdString: string,
      values: ReProductionBasicInfoFormData
    ) => {
      const savedProductionId = Number(savedProductionIdString);
      const hasNumericId =
        Number.isFinite(savedProductionId) && savedProductionId > 0;

      reset(values, {
        keepDirty: false,
        keepDefaultValues: false,
      });

      onFormDataChange(
        {
          ...(hasNumericId ? {productionId: savedProductionId} : {}),
          name: values.name ?? '',
          description: values.description ?? '',
          reProductionType: values.reProductionType ?? null,
        },
        {silent: true}
      );

      setIsSaved(true);

      const encryptedIdForQuery = savedProductionIdString?.trim() ?? '';
      if (shouldNavigateToEdit() && encryptedIdForQuery) {
        navigate(
          `/Production/Home/ReProduction/Edit?productionId=${encodeURIComponent(encryptedIdForQuery)}`,
          {state: {justSaved: true}}
        );
      }
    };

    useImperativeHandle(ref, () => ({
      save: async () => {
        const valid = await trigger();
        if (!valid) return;

        const values = getValues();
        const payload = buildPayload(values, false);
        if (!payload) return;

        saveReProductionBasicInfo(payload, {
          onSuccess: (savedData) => {
            const encryptedId = getEncryptedIdFromSaveBasicInfoResponse(
              savedData
            );
            handleSaveSuccess(encryptedId, values);
            getStatus(
              true,
              ToastType.Success,
              t('reProduction.toastMessages.saveSuccess')
            );
          },
          onError: (err) => {
            getStatus(true, ToastType.Error, err?.message);
          },
        });

        // Clear the unsaved-changes flag synchronously so PrivateRoute re-renders and
        // refreshes `blockNavigation` before the API response + navigate() lands.
        dispatch(setReproductionIsFieldsChanged({isFieldsChanged: false}));
      },
      next: async () => {
        if (isInProgressOrCompleted) {
          goNext();
          return;
        }
        const valid = await trigger();
        if (!valid) return;

        const values = getValues();
        const payload = buildPayload(values, true);
        if (!payload) return;

        saveReProductionBasicInfo(payload, {
          onSuccess: (savedData) => {
            const encryptedId = getEncryptedIdFromSaveBasicInfoResponse(
              savedData
            );
            handleSaveSuccess(encryptedId, values);
            getStatus(
              true,
              ToastType.Success,
              t('reProduction.toastMessages.nextSuccess')
            );
            goNext();
          },
          onError: (err) => {
            getStatus(true, ToastType.Error, err?.message);
          },
        });

        dispatch(setReproductionIsFieldsChanged({isFieldsChanged: false}));
      },
    }));

    return (
      <Box
        className="re-production-basic-info"
        data-testid="re-production-basic-info"
      >
        <Box
          className="re-production-basic-info__header"
          data-testid="re-production-basic-info-header"
        >
          <Typography
            className="re-production-basic-info__header-title"
            data-testid="re-production-basic-info-header-title"
          >
            {t('reProduction.basicInfo.header.title')}
          </Typography>
        </Box>

        <Box className="re-production-basic-info__form">
          <Box className="re-production-basic-info__left">
            <Box className="re-production-basic-info__field-group">
              <Typography
                className="typo-text label2_size"
                data-testid="re-production-basic-info-name-label"
              >
                {`${t('reProduction.basicInfo.form.name.label')}*`}
              </Typography>
              <ArrTextField
                dataTestId="re-production-name"
                id="re-production-name"
                data-testid="re-production-name-input"
                state={
                  isInProgressOrCompleted
                    ? 'inactive'
                    : errors.name
                      ? 'error'
                      : 'enabled'
                }
                placeholder={t('reProduction.basicInfo.form.name.placeholder')}
                {...register('name')}
                supportText={errors.name?.message || ''}
              />
            </Box>

            <Box className="re-production-basic-info__field-group">
              <Typography
                className="typo-text label2_size"
                data-testid="re-production-basic-info-description-label"
              >
                {t('reProduction.basicInfo.form.description.label')}
              </Typography>
              <ArrTextField
                dataTestId="re-production-description"
                id="re-production-description"
                data-testid="re-production-description-input"
                state={
                  isInProgressOrCompleted
                    ? 'inactive'
                    : errors.description
                      ? 'error'
                      : 'enabled'
                }
                placeholder={t(
                  'reProduction.basicInfo.form.description.placeholder'
                )}
                {...register('description')}
                supportText={errors.description?.message || ''}
                multiline
                maxRows={4}
                fullWidth
                slotProps={{
                  input: {
                    style: {
                      resize: 'vertical',
                    },
                  },
                }}
              />
            </Box>
          </Box>

          <Box className="re-production-basic-info__right">
            <Typography
              className="re-production-basic-info__type-title"
              data-testid="re-production-basic-info-types-title"
            >
              {`${t('reProduction.basicInfo.form.types.label')}*`}
            </Typography>

            <Controller
              name="reProductionType"
              control={control}
              render={({field, fieldState}) => (
                <Box className="re-production-basic-info__type-options">
                  {typeOptions.map((option) => {
                    const isSelected = field.value === option.value;
                    const optionClasses = [
                      're-production-basic-info__type-option',
                      isSelected
                        ? 're-production-basic-info__type-option--selected'
                        : '',
                    ]
                      .filter(Boolean)
                      .join(' ');

                    return (
                      <Box
                        key={option.value}
                        className={optionClasses}
                        onClick={() => field.onChange(option.value)}
                        sx={{
                          pointerEvents: isInProgressOrCompleted ? 'none' : 'auto',
                          opacity: isInProgressOrCompleted ? 0.7 : 1,
                        }}
                        data-testid={`type-option-${option.value}`}
                      >
                        <Radio
                          checked={isSelected}
                          onChange={() => field.onChange(option.value)}
                          value={option.value}
                          icon={<RadioEnableIcon />}
                          checkedIcon={<RadioActiveIcon />}
                          className="custom-radio-label"
                          disabled={isInProgressOrCompleted}
                        />
                        <Box className="re-production-basic-info__type-option-content">
                          <Typography
                            className="re-production-basic-info__type-option-label"
                            data-testid={`type-option-label-${option.value}`}
                          >
                            {option.label}
                          </Typography>
                          <Typography
                            className="re-production-basic-info__type-option-description"
                            data-testid={`type-option-description-${option.value}`}
                          >
                            {option.description}
                          </Typography>
                        </Box>
                      </Box>
                    );
                  })}
                  {fieldState.error && (
                    <Typography
                      className="re-production-basic-info__type-error"
                      data-testid="type-error"
                    >
                      {fieldState.error.message}
                    </Typography>
                  )}
                </Box>
              )}
            />
          </Box>
        </Box>
      </Box>
    );
  }
);

ReProductionBasicInfo.displayName = 'ReProductionBasicInfo';

export default ReProductionBasicInfo;
